import React, { useState, useMemo, useEffect } from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  styled,
  useTheme,
  Box,
  useMediaQuery,
  Button,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CodeIcon from "@mui/icons-material/Code";
import { Cadet } from "../shared/types/scoreboard.types";
import { DesktopDrawerComponent } from "./scoreboard/DesktopDrawerComponent";
import { MobileModalComponent } from "./scoreboard/MobileModalComponent";
import { ScoreboardRow } from "./scoreboard/ScoreboardRow";
import { exportToCSV, exportToFormattedExcel } from "../utils/exportUtils";

const StyledHeader = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold",
  backgroundColor: theme.palette.grey[100],
  position: "sticky",
  top: 0,
  zIndex: 1,
}));

interface ScoreboardFullDetailsTableProps {
  data: Cadet[];
}

export const ScoreboardFullDetailsTable: React.FC<ScoreboardFullDetailsTableProps> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [order] = useState<"asc" | "desc">("asc");
  const [orderBy] = useState<keyof Cadet>("rank");
  const [selectedCadet, setSelectedCadet] = useState<Cadet | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [permanentDrawer, setPermanentDrawer] = useState(false);

  // Process, sort and filter data
  const processedData = useMemo(() => {
    let result = [...data];

    result.sort((a, b) => {
      if (a.rank === 999 && b.rank === 999) {
        return b.total - a.total;
      } else if (a.rank === 999) {
        return 1;
      } else if (b.rank === 999) {
        return -1;
      } else if (a.rank !== b.rank) {
        return a.rank - b.rank;
      } else {
        return a.primaryCadet.localeCompare(b.primaryCadet);
      }
    });

    return result;
  }, [data, order, orderBy]);

  const handleRowClick = (cadet: Cadet) => {
    setSelectedCadet(cadet);
  };

  const handleCloseDrawer = () => {
    setSelectedCadet(null);
  };

  return (
    <>
      {/* {!isMobile && (
        <>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            sx={{ marginBottom: "20px", marginRight: "10px" }}
            onClick={() => exportToCSV(processedData)}>
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<CodeIcon />}
            sx={{ marginBottom: "20px" }}
            onClick={() => exportToFormattedExcel(processedData)}>
            Export HTML
          </Button>
        </>
      )} */}

      <Paper
        sx={{
          width: permanentDrawer ? "60%" : "100%",
          overflow: "hidden",
          transition: "width 0.2s ease",
          "& .MuiTableCell-root": {
            padding: isMobile ? "8px 4px" : "8px 16px",
            fontSize: isMobile ? "0.75rem" : "0.875rem",
          },
        }}>
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 200px)",
            overflow: "auto",
            "&::-webkit-scrollbar": {
              height: "8px",
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "4px",
            },
          }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledHeader></StyledHeader>
                <StyledHeader>Rank</StyledHeader>
                <StyledHeader>Primary</StyledHeader>
                <StyledHeader>Spouse/Partner</StyledHeader>
                <StyledHeader>SMD</StyledHeader>
                <StyledHeader sx={{ minWidth: isMobile ? "80px" : "auto" }}>Qual</StyledHeader>
                <StyledHeader>C1</StyledHeader>
                <StyledHeader>C2</StyledHeader>
                <StyledHeader>C3</StyledHeader>
                <StyledHeader>C4</StyledHeader>
                <StyledHeader>C5</StyledHeader>
                <StyledHeader>Total</StyledHeader>
                <StyledHeader sx={{ width: "40px" }}></StyledHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedData.map((cadet, index) => (
                <ScoreboardRow cadet={cadet} index={index} onRowClick={handleRowClick} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <DesktopDrawerComponent
        selectedCadet={selectedCadet}
        onCloseDrawer={handleCloseDrawer}
        permanentDrawer={permanentDrawer}
        setPermanentDrawer={setPermanentDrawer}
      />
      <MobileModalComponent selectedCadet={selectedCadet} onCloseDrawer={handleCloseDrawer} />
    </>
  );
};
